/**
 * De authenticatiestaat van de applicatie: het type, de context en de hook.
 *
 * De tokenprovider (ADR-003) weet hóé je aan een token komt. Deze staat beschrijft
 * wát de gebruiker daarvan merkt: wordt er nog opgestart, is er niemand ingelogd,
 * is de configuratie stuk, of accepteert de backend het token niet. Die vier
 * gevallen zien er verschillend uit en vragen om een verschillende volgende stap
 * — één generieke foutmelding zou ze op een hoop gooien.
 *
 * De backend blijft de autoriteit. Wat hier staat bepaalt alleen wat het scherm
 * toont; elk verzoek wordt daar opnieuw op het access-token beoordeeld. Een
 * frontend die zijn eigen rollen gelooft, beschermt niets.
 *
 * Los van `AuthProvider.tsx` omdat een module die zowel een component als een
 * hook exporteert, Fast Refresh breekt.
 */
import { createContext, use } from "react";

import type { AtlasAccount, AuthMode } from "./tokenProvider";

export type AuthStatus =
  /** De provider start op; MSAL wisselt eventueel een autorisatiecode in. */
  | "initialising"
  /** Er is niemand ingelogd. Het inlogscherm biedt de knop aan. */
  | "signed-out"
  /** Er is iemand ingelogd; de applicatie mag renderen. */
  | "signed-in"
  /** De gekozen modus mist configuratie — een fout van de build, niet van de gebruiker. */
  | "misconfigured"
  /** Ingelogd, maar de backend wijst het token af (401): geen tenant, of verlopen. */
  | "denied";

export type AuthState = {
  status: AuthStatus;
  mode: AuthMode;
  /** Menselijke naam van de modus, voor het gebruikersmenu. */
  label: string;
  account: AtlasAccount | null;
  /** Toelichting bij `misconfigured` en `denied`; anders `null`. */
  problem: string | null;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
};

export const AuthContext = createContext<AuthState | null>(null);

export function useAuth(): AuthState {
  const value = use(AuthContext);
  if (!value) {
    throw new Error("useAuth moet binnen <AuthProvider> worden gebruikt.");
  }
  return value;
}

/** Sleutel waaronder het scherm wordt onthouden waar iemand heen wilde. */
const RETURN_TO_KEY = "atlas.auth.returnTo";

/**
 * Het al opgehaalde pad. `undefined` betekent "nog niet gekeken".
 *
 * Nodig omdat `takeReturnTo()` tijdens het renderen wordt aangeroepen en de
 * waarde wist: `StrictMode` rendert een component twee keer en gebruikt de
 * uitkomst van de tweede ronde, die zonder deze buffer altijd leeg zou zijn.
 * De gebruiker landde dan op het dashboard in plaats van op het scherm dat hij
 * had gevraagd.
 */
let taken: string | null | undefined;

/**
 * Onthoudt waar de gebruiker heen wilde vóór het inloggen.
 *
 * Inloggen bij Entra gaat via een volledige paginanavigatie naar Microsoft en
 * terug. Alles in het geheugen van de applicatie is dan weg, dus het pad moet
 * die reis buiten React overleven. `sessionStorage` en niet `localStorage`: dit
 * hoort bij één tabblad en één sessie.
 */
export const rememberReturnTo = (path: string): void => {
  taken = undefined;
  try {
    globalThis.sessionStorage?.setItem(RETURN_TO_KEY, path);
  } catch {
    /* geen opslag: dan landt de gebruiker straks op het dashboard */
  }
};

/**
 * Leest het onthouden scherm en wist het uit de opslag — het geldt één keer,
 * tot `rememberReturnTo()` opnieuw iets vastlegt. Herhaald aanroepen levert
 * hetzelfde antwoord, zodat hij veilig tijdens het renderen kan worden gebruikt.
 */
export const takeReturnTo = (): string | null => {
  if (taken !== undefined) return taken;
  try {
    const value = globalThis.sessionStorage?.getItem(RETURN_TO_KEY) ?? null;
    globalThis.sessionStorage?.removeItem(RETURN_TO_KEY);
    // Alleen een pad binnen deze applicatie. Een waarde die met `//` of met een
    // schema begint zou de gebruiker na het inloggen naar een andere site
    // kunnen sturen; dat is precies de open redirect die je niet wilt.
    taken = value && value.startsWith("/") && !value.startsWith("//") ? value : null;
  } catch {
    taken = null;
  }
  return taken;
};
